// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        shipTo: getShipToState(state, cart?.shipToId)?.value,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationBillingAndShippingStyles {
    container?: GridContainerProps;
    shipToGridItem?: GridItemProps;
    shipToTitleText?: TypographyPresentationProps;
    shipToText?: TypographyPresentationProps;
    shipToAddress?: AddressInfoDisplayStyles;
    pickUpGridItem?: GridItemProps;
    pickUpTitleText?: TypographyPresentationProps;
    pickUpText?: TypographyPresentationProps;
    pickUpAddress?: AddressInfoDisplayStyles;
    billToGridItem?: GridItemProps;
    billToTitleText?: TypographyPresentationProps;
    billToText?: TypographyPresentationProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    titleGridItem?: InjectableCss;
    addressContainer?: InjectableCss;
}

export const billingAndShippingStyles: OnePageCheckoutConfirmationBillingAndShippingStyles = {
    container: { gap: 20 },
    shipToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    shipToTitleText: {
        variant: "h5",
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    pickUpTitleText: {
        variant: "h5",
    },
    billToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    billToTitleText: {
        variant: "h5",
    },
};

const styles = billingAndShippingStyles;

const OnePageCheckoutConfirmationBillingAndShipping = ({ cart, billTo, shipTo }: Props) => {
    if (!cart || !billTo || !shipTo) {
        return null;
    }
    // XNG-Change: Add Title, change Titles
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.titleGridItem}>
                <Typography {...styles.title}>{translate("Addresses")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.addressContainer}>
                <StyledWrapper
                    {...styles.billToGridItem}
                    data-test-selector="onePageCheckOutConfirmationBillingInformation"
                >
                    <Typography {...styles.billToTitleText}>{translate("Billing Address")}</Typography>
                    <Typography as="p" {...styles.billToText}>
                        <AddressInfoDisplay
                            {...billTo}
                            state={billTo.state?.abbreviation}
                            country={billTo.country?.name}
                        />
                    </Typography>
                </StyledWrapper>
                {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <StyledWrapper
                        {...styles.shipToGridItem}
                        data-test-selector="onePageCheckOutConfirmationShippingInformation"
                    >
                        <Typography {...styles.shipToTitleText}>{translate("Shipping Address")}</Typography>
                        <AddressInfoDisplay
                            {...shipTo}
                            state={shipTo.state?.abbreviation}
                            country={shipTo.country?.name}
                            extendedStyles={styles.shipToAddress}
                        />
                    </StyledWrapper>
                )}
                {cart.fulfillmentMethod === FulfillmentMethod.PickUp && cart.defaultWarehouse && (
                    <StyledWrapper {...styles.pickUpGridItem}>
                        <Typography as="h3" {...styles.pickUpTitleText}>
                            {translate("Pick Up Location")}
                        </Typography>
                        <AddressInfoDisplay
                            {...cart.defaultWarehouse}
                            companyName={cart.defaultWarehouse?.description || cart.defaultWarehouse?.name}
                            extendedStyles={styles.pickUpAddress}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationBillingAndShipping),
    definition: {
        displayName: "Billing And Shipping",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
